// Edit-buffer reducer for ScreenDetailsPage. Holds all the unpublished
// fields the operator can modify before pressing Publish. Server data
// (the loaded `screen`, `branches`, etc.) is kept outside this reducer.

export type ItemRef    = { id: number; name: string }
export type MediaRef   = { id: number; original_name: string; file_type: string; url: string; file_size?: number; created_at?: string }
export type PlaylistRef = { id: number; name: string; media: MediaRef[]; media_durations: Record<number, number> }

export type OrientationType = 'horizontal' | 'vertical'
export type RotationDegrees = 0 | 90 | 180 | 270
// How the player frames each content item against the screen. 'default' =
// contain (letterbox, whole item visible); 'fit' = cover (fill the screen,
// cropping overflow). Wire values match Screen.display_settings.scaling.
export type ContentScaling  = 'default' | 'fit'

export interface EditBuffer {
  // Overview form
  name:        string
  branchId:    string
  // Two-axis orientation. `orientationType` picks the content aspect
  // (16:9 vs 9:16); `rotationDegrees` picks the output rotation. The
  // combination decides how the player frames + rotates content.
  orientationType: OrientationType
  rotationDegrees: RotationDegrees
  location:    string
  isActive:    boolean

  // Settings tab
  audioMode:           'muted' | 'unmute'
  // Mute the video content's own audio track, independently of the master
  // audio channel above. Persisted in display_settings.video_muted.
  videoMuted:          boolean
  // Web player only: hides its fullscreen/install overlay and the menu's
  // fullscreen row. Android has no browser chrome and ignores it.
  hidePlayerControls:  boolean
  // Master audio level 0–100, applied when the screen is unmuted. Persisted in
  // display_settings.volume and delivered to players in the content manifest.
  volume:              number
  scaling:             ContentScaling
  offlineAlertMinutes: number | null

  // Content selection (selected*Ids are kept in sync with assigned* — both
  // are needed: assigned* drives the picker exclude lists; selected*Ids is
  // the wire format sent on save).
  assignedMenus:     ItemRef[]
  assignedMedia:     MediaRef[]
  assignedPlaylists: PlaylistRef[]
  selectedMenuIds:     Set<number>
  selectedMediaIds:    Set<number>
  selectedPlaylistIds: Set<number>
  menuDurations:  Record<number, number>
  mediaDurations: Record<number, number>
}

export const emptyEditBuffer: EditBuffer = {
  name: '', branchId: '',
  orientationType: 'horizontal', rotationDegrees: 0,
  location: '', isActive: true,
  audioMode: 'unmute', videoMuted: false, hidePlayerControls: false,
  volume: 100, scaling: 'default', offlineAlertMinutes: null,
  assignedMenus: [], assignedMedia: [], assignedPlaylists: [],
  selectedMenuIds: new Set(), selectedMediaIds: new Set(), selectedPlaylistIds: new Set(),
  menuDurations: {}, mediaDurations: {},
}

// Hydrate payload — the subset of fields a server snapshot can populate.
export type HydratePayload = Partial<EditBuffer>

// Picker payload — used by handlePickerConfirm to append fresh content.
export interface PickerAddition {
  media:     MediaRef[]
  playlists: PlaylistRef[]
  // Default durations for newly-added media (id → seconds). If omitted,
  // the reducer defaults each new id to 10 to match the player default.
  mediaDurations?: Record<number, number>
}

export type EditAction =
  | { type: 'hydrate'; payload: HydratePayload }
  | { type: 'setName';                value: string }
  | { type: 'setBranchId';            value: string }
  | { type: 'setOrientationType';     value: OrientationType }
  | { type: 'setRotationDegrees';     value: RotationDegrees }
  | { type: 'setLocation';            value: string }
  | { type: 'setIsActive';            value: boolean }
  | { type: 'setAudioMode';           value: 'muted' | 'unmute' }
  | { type: 'setVideoMuted';          value: boolean }
  | { type: 'setHidePlayerControls';  value: boolean }
  | { type: 'setVolume';              value: number }
  | { type: 'setScaling';             value: ContentScaling }
  | { type: 'setOfflineAlertMinutes'; value: number | null }
  | { type: 'addPickerContent';       payload: PickerAddition }
  | { type: 'removeContent';          payload: { mediaIds?: number[]; playlistIds?: number[]; menuIds?: number[] } }

export function editBufferReducer(state: EditBuffer, action: EditAction): EditBuffer {
  switch (action.type) {
    case 'hydrate':
      return { ...state, ...action.payload }
    case 'setName':                return { ...state, name: action.value }
    case 'setBranchId':            return { ...state, branchId: action.value }
    case 'setOrientationType':     return { ...state, orientationType: action.value }
    case 'setRotationDegrees':     return { ...state, rotationDegrees: action.value }
    case 'setLocation':            return { ...state, location: action.value }
    case 'setIsActive':            return { ...state, isActive: action.value }
    case 'setAudioMode':           return { ...state, audioMode: action.value }
    case 'setVideoMuted':          return { ...state, videoMuted: action.value }
    case 'setHidePlayerControls':  return { ...state, hidePlayerControls: action.value }
    case 'setVolume':              return { ...state, volume: Math.max(0, Math.min(100, Math.round(action.value) || 0)) }
    case 'setScaling':             return { ...state, scaling: action.value }
    case 'setOfflineAlertMinutes': return { ...state, offlineAlertMinutes: action.value }
    case 'removeContent': {
      const { mediaIds = [], playlistIds = [], menuIds = [] } = action.payload
      const mIds = new Set(mediaIds)
      const pIds = new Set(playlistIds)
      const muIds = new Set(menuIds)
      const next: EditBuffer = { ...state }
      if (mIds.size) {
        next.selectedMediaIds = new Set([...state.selectedMediaIds].filter(id => !mIds.has(id)))
        next.assignedMedia    = state.assignedMedia.filter(m => !mIds.has(m.id))
      }
      if (pIds.size) {
        next.selectedPlaylistIds = new Set([...state.selectedPlaylistIds].filter(id => !pIds.has(id)))
        next.assignedPlaylists   = state.assignedPlaylists.filter(p => !pIds.has(p.id))
      }
      if (muIds.size) {
        next.selectedMenuIds = new Set([...state.selectedMenuIds].filter(id => !muIds.has(id)))
        next.assignedMenus   = state.assignedMenus.filter(m => !muIds.has(m.id))
      }
      return next
    }
    case 'addPickerContent': {
      const { media, playlists, mediaDurations } = action.payload
      const next = { ...state }
      if (media.length) {
        next.selectedMediaIds = new Set([...state.selectedMediaIds, ...media.map(m => m.id)])
        next.assignedMedia    = [...state.assignedMedia, ...media]
        const dur = { ...state.mediaDurations }
        for (const m of media) {
          if (dur[m.id] === undefined) dur[m.id] = mediaDurations?.[m.id] ?? 10
        }
        next.mediaDurations = dur
      }
      if (playlists.length) {
        next.selectedPlaylistIds = new Set([...state.selectedPlaylistIds, ...playlists.map(p => p.id)])
        const seen = new Set(state.assignedPlaylists.map(p => p.id))
        const merged = [...state.assignedPlaylists]
        for (const pl of playlists) {
          if (!seen.has(pl.id)) merged.push(pl)
        }
        next.assignedPlaylists = merged
      }
      return next
    }
  }
}

// Build a hydrate payload from a Screen API response + a known playlist
// catalog (needed because the server only sends playlist ids/refs on Screen,
// not the full PlaylistRef shape).
export function hydrateFromScreen(
  scr: any,
  allPlaylists: PlaylistRef[],
): HydratePayload {
  const ds = scr.display_settings ?? {}
  const scrPlaylistIds = new Set<number>((scr.playlists ?? []).map((p: any) => p.id))
  // Coerce server values into the typed union — anything unexpected falls
  // back to (horizontal, 0) so a malformed row can't blow up the reducer.
  const type: OrientationType = scr.orientation_type === 'vertical' ? 'vertical' : 'horizontal'
  const rotRaw = Number(scr.rotation_degrees ?? 0)
  const rot: RotationDegrees =
    rotRaw === 90 || rotRaw === 180 || rotRaw === 270 ? rotRaw : 0
  return {
    name:        scr.name,
    branchId:    scr.branch_id?.toString() ?? '',
    orientationType: type,
    rotationDegrees: rot,
    location:    scr.location ?? '',
    isActive:    scr.is_active,
    // Screens default to unmuted: only an explicit `false` shows as muted.
    audioMode:   ds.audio_unmuted === false ? 'muted' : 'unmute',
    // Video sound plays by default; only an explicit `true` mutes it.
    videoMuted:  ds.video_muted === true,
    hidePlayerControls: ds.hide_player_controls === true,
    // Master volume 0–100; default full when unset/malformed.
    volume:      typeof ds.volume === 'number' ? Math.max(0, Math.min(100, Math.round(ds.volume))) : 100,
    // Content framing: only an explicit 'fit' (cover) overrides the contain default.
    scaling:     ds.scaling === 'fit' ? 'fit' : 'default',
    offlineAlertMinutes: typeof scr.offline_alert_minutes === 'number' ? scr.offline_alert_minutes : null,
    assignedMenus:     scr.menus ?? [],
    assignedMedia:     scr.media ?? [],
    assignedPlaylists: allPlaylists.filter(p => scrPlaylistIds.has(p.id)),
    selectedMenuIds:     new Set<number>((scr.menus ?? []).map((m: any) => m.id)),
    selectedMediaIds:    new Set<number>((scr.media ?? []).map((m: any) => m.id)),
    selectedPlaylistIds: scrPlaylistIds,
    menuDurations:  scr.menu_durations  ?? {},
    mediaDurations: scr.media_durations ?? {},
  }
}
